import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  signal,
  computed,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';
import { User } from '../../../core/models/user.model';
import { RouteBuilder } from '../../../core/constants/routes.constants';

export interface NavigationItem {
  icon: string;
  label: string;
  route: string;
  active?: boolean;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  roles?: string[];
}

export interface BottomNavigationConfig {
  showNavigation?: boolean;
  items?: NavigationItem[];
  backgroundColor?: string;
  textColor?: string;
  activeColor?: string;
  showLabels?: boolean;
  showIcons?: boolean;
  position?: 'bottom' | 'top';
  height?: string;
  shadow?: boolean;
  border?: boolean;
}

@Component({
  selector: 'app-bottom-navigation',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  template: `
    <nav
      *ngIf="effectiveConfig.showNavigation"
      [class]="getNavigationClasses()"
    >
      <div class="max-w-screen-xl mx-auto">
        <div class="nav-content flex justify-around items-center py-2">
          <!-- Left nav items (before center button) -->
          <ng-container *ngFor="let item of leftItems(); let i = index">
            <a
              [routerLink]="item.route"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: item.route === '/' }"
              [class]="getItemClasses(item, i)"
              (click)="onItemClick(item, i)"
              [attr.data-item-index]="i"
            >
              <div
                *ngIf="effectiveConfig.showIcons"
                class="flex justify-center items-center mb-1"
              >
                <i [class]="getIconClasses(item, i)"></i>
              </div>
              <span
                *ngIf="effectiveConfig.showLabels"
                [class]="getLabelClasses()"
              >
                {{ item.label | translate }}
              </span>
            </a>
          </ng-container>

          <!-- Center camera button -->
          <button
            class="submit-offer-btn-wrapper"
            (click)="navigateToSubmitOffer()"
            aria-label="Subir oferta"
          >
            <div class="submit-offer-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span class="submit-offer-label">{{ 'nav.submit' | translate }}</span>
          </button>

          <!-- Right nav items (after center button) -->
          <ng-container *ngFor="let item of rightItems(); let i = index">
            <a
              [routerLink]="item.route"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: item.route === '/' }"
              [class]="getItemClasses(item, i + leftItems().length)"
              (click)="onItemClick(item, i + leftItems().length)"
              [attr.data-item-index]="i + leftItems().length"
            >
              <div
                *ngIf="effectiveConfig.showIcons"
                class="flex justify-center items-center mb-1"
              >
                <i [class]="getIconClasses(item, i + leftItems().length)"></i>
              </div>
              <span
                *ngIf="effectiveConfig.showLabels"
                [class]="getLabelClasses()"
              >
                {{ item.label | translate }}
              </span>
            </a>
          </ng-container>
        </div>
      </div>
    </nav>
  `,
  styles: [
    `
      /* Host element must be block for flex layout */
      :host {
        display: block;
        flex-shrink: 0;
        --gastronauta-bottom-nav-height: calc(
          4rem + env(safe-area-inset-bottom, 0px)
        );
      }

      /* Safe area padding for iOS/Android home indicator */
      nav {
        position: relative;
        padding-bottom: var(
          --safe-area-inset-bottom,
          env(safe-area-inset-bottom, 0px)
        );

        /* Expose nav height as CSS variable for other components to use */
        --bottom-nav-height: calc(4rem + env(safe-area-inset-bottom, 0px));
      }

      .nav-content {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }

      .active {
        color: var(--active-color, #e91d63);
      }

      .nav-item {
        transition: all 0.2s ease-in-out;
        border-radius: 0.5rem;
        padding: 0.5rem;
        border: none;
        outline: none;
      }

      .nav-item:hover {
        transform: translateY(-1px);
      }

      .nav-item.active {
        transform: translateY(-2px);
        border: none;
        outline: none;
      }

      .nav-item.active .nav-icon {
        transform: scale(1.1);
        color: #e91d63;
      }

      .nav-item.active span {
        color: #e91d63;
        font-weight: 600;
      }

      .nav-shadow {
        box-shadow:
          0 -4px 6px -1px rgba(0, 0, 0, 0.1),
          0 -2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .nav-border {
        border-top: 1px solid #e5e7eb;
      }

      .submit-offer-btn-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        outline: none;
        margin-top: -20px;
      }

      .submit-offer-circle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 52px;
        height: 52px;
        border-radius: 50%;
        background: #e91d63;
        color: white;
        border: 4px solid white;
        box-shadow: 0 4px 12px rgba(233, 29, 99, 0.4);
        transition: all 0.2s ease-in-out;
        flex-shrink: 0;
      }

      .submit-offer-btn-wrapper:hover .submit-offer-circle {
        background: #c2185b;
        box-shadow: 0 6px 16px rgba(233, 29, 99, 0.5);
        transform: translateY(-2px);
      }

      .submit-offer-btn-wrapper:active .submit-offer-circle {
        transform: translateY(1px);
        box-shadow: 0 2px 8px rgba(233, 29, 99, 0.3);
      }

      .submit-offer-label {
        font-size: 0.625rem;
        font-weight: 600;
        color: #e91d63;
        margin-top: 2px;
        white-space: nowrap;
      }
    `,
  ],
})
export class BottomNavigationComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() config: BottomNavigationConfig = {};

  // Angular 19 signals
  private authService = inject(AuthService);
  private router = inject(Router);
  private el = inject(ElementRef);

  // Signals for reactive state management (derived from AuthService)
  private isAuthenticated = computed(() => this.authService.isAuthenticated());
  private isAdmin = computed(() => this.authService.isAdmin());
  private currentUser = computed(() => this.authService.currentUser());
  private currentRoute = signal('');

  // Default configuration
  private defaultConfig: BottomNavigationConfig = {
    showNavigation: true,
    items: [
      {
        icon: 'fas fa-tags',
        label: 'nav.offers',
        route: '/offers',
        requiresAuth: false,
      },
      {
        icon: 'fas fa-utensils',
        label: 'nav.menus',
        route: '/menus',
        requiresAuth: false,
      },
      {
        icon: 'fas fa-map-marker-alt',
        label: 'nav.map',
        route: '/places',
        requiresAuth: false,
      },
      {
        icon: 'fas fa-heart',
        label: 'nav.favorites',
        route: '/liked-offers',
        requiresAuth: false,
      },
    ],
    backgroundColor: 'bg-white',
    textColor: 'text-gray-700',
    activeColor: 'text-primary-600',
    showLabels: true,
    showIcons: true,
    position: 'bottom',
    height: '4rem',
    shadow: true,
    border: true,
  };

  // Computed signal for filtered navigation items based on authorization
  filteredItems = computed(() => {
    const items = this.effectiveConfig.items || [];
    return items.filter((item) => this.canAccessItem(item));
  });

  leftItems = computed(() => {
    const items = this.filteredItems();
    const mid = Math.ceil(items.length / 2);
    return items.slice(0, mid);
  });

  rightItems = computed(() => {
    const items = this.filteredItems();
    const mid = Math.ceil(items.length / 2);
    return items.slice(mid);
  });

  constructor() {}

  ngOnInit() {
    // Listen to route changes to update active state
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.url);
        this.updateActiveItems();
      });
  }

  ngAfterViewInit() {
    // Measure the actual height of the navigation bar and expose it as a CSS variable
    // This creates an "anchor" system for other components to position elements above the nav
    this.updateNavHeightVariable();

    // Watch for size changes (orientation, keyboard, etc.)
    if (typeof ResizeObserver !== 'undefined') {
      const navElement = this.el.nativeElement.querySelector('nav');
      if (navElement) {
        const resizeObserver = new ResizeObserver(() => {
          this.updateNavHeightVariable();
        });
        resizeObserver.observe(navElement);

        // Store the observer to disconnect it later
        (this as any)._resizeObserver = resizeObserver;
      }
    }
  }

  ngOnDestroy() {
    // Disconnect ResizeObserver if it exists
    if ((this as any)._resizeObserver) {
      (this as any)._resizeObserver.disconnect();
    }
  }

  /**
   * Measures the navigation bar's actual height and sets it as a CSS variable
   * This allows other components to position elements relative to the nav bar
   */
  private updateNavHeightVariable() {
    const navElement = this.el.nativeElement.querySelector('nav');
    if (navElement) {
      const rect = navElement.getBoundingClientRect();
      const height = rect.height;

      // Set CSS variable on document root for global access
      document.documentElement.style.setProperty(
        '--bottom-nav-height',
        `${height}px`,
      );

      console.log('🎯 Bottom nav height set as CSS variable:', `${height}px`);
    }
  }

  get effectiveConfig(): BottomNavigationConfig {
    return { ...this.defaultConfig, ...this.config };
  }

  getNavigationClasses(): string {
    const classes = [
      'w-full z-40',
      'bg-white',
      'border-t border-gray-200',
      this.effectiveConfig.backgroundColor,
      this.effectiveConfig.textColor,
    ];

    if (this.effectiveConfig.shadow) {
      classes.push('shadow-lg');
    }

    if (this.effectiveConfig.border) {
      classes.push('border-t');
    }

    return classes.join(' ');
  }

  getItemClasses(item: NavigationItem, index: number): string {
    const classes = [
      'flex flex-col items-center py-2 px-3 rounded-lg transition-all duration-200 nav-item',
      'cursor-pointer',
      'focus:outline-none',
    ];

    return classes.join(' ');
  }

  getIconClasses(item: NavigationItem, index: number): string {
    const classes = [
      'block w-6 h-6 text-center nav-icon transition-transform duration-200',
      item.icon,
    ];

    return classes.join(' ');
  }

  getLabelClasses(): string {
    return 'text-xs font-medium';
  }

  navigateToSubmitOffer() {
    this.router.navigate([RouteBuilder.submitOffer()]);
  }

  onItemClick(item: NavigationItem, index: number) {
    // Update active state
    this.updateActiveItems();

    // Emit custom event if needed
    this.emitNavigationEvent(item, index);
  }

  private updateActiveItems() {
    // Update active state based on current route
    this.effectiveConfig.items?.forEach((item, index) => {
      item.active = this.isItemActive(item);
    });
  }

  private isItemActive(item: NavigationItem): boolean {
    const currentRoute = this.currentRoute();
    if (item.route === '/') {
      return currentRoute === '/';
    }
    return currentRoute.startsWith(item.route);
  }

  // Authorization logic using Angular 19 approach
  private canAccessItem(item: NavigationItem): boolean {
    // If no auth requirements, allow access
    if (!item.requiresAuth && !item.requiresAdmin && !item.roles?.length) {
      return true;
    }

    const isAuth = this.isAuthenticated();
    const isAdmin = this.isAdmin();
    const user = this.currentUser();

    // Check authentication requirement
    if (item.requiresAuth && !isAuth) {
      return false;
    }

    // Check admin requirement
    if (item.requiresAdmin && !isAdmin) {
      return false;
    }

    // Check role requirements
    if (item.roles?.length) {
      const userRole = user?.role;
      if (!userRole) {
        return false;
      }
      const hasRequiredRole = item.roles.includes(userRole);
      if (!hasRequiredRole) {
        return false;
      }
    }

    return true;
  }

  private emitNavigationEvent(item: NavigationItem, index: number) {
    // Dispatch custom event for external listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('bottomNavigationClick', {
          detail: {
            item,
            index,
            route: item.route,
          },
        }),
      );
    }
  }

  // Public methods for external control
  setActiveItem(index: number) {
    if (
      this.effectiveConfig.items &&
      index >= 0 &&
      index < this.effectiveConfig.items.length
    ) {
      this.effectiveConfig.items.forEach((item, i) => {
        item.active = i === index;
      });
    }
  }

  updateItems(newItems: NavigationItem[]) {
    if (newItems && Array.isArray(newItems)) {
      this.config = {
        ...this.config,
        items: newItems,
      };
    }
  }

  toggleVisibility() {
    this.config = {
      ...this.config,
      showNavigation: !this.effectiveConfig.showNavigation,
    };
  }

  // Animation methods
  triggerItemAnimation(index: number) {
    // Add animation class temporarily
    const element = document.querySelector(`[data-item-index="${index}"]`);
    if (element) {
      element.classList.add('animate-pulse');
      setTimeout(() => {
        element.classList.remove('animate-pulse');
      }, 300);
    }
  }

  // Configuration presets
  static getPresets() {
    return {
      minimal: {
        showLabels: false,
        height: '3rem',
        items: [
          { icon: 'fas fa-home', label: 'Home', route: '/' },
          { icon: 'fas fa-search', label: 'Search', route: '/search' },
          { icon: 'fas fa-user', label: 'Profile', route: '/profile' },
        ],
      },
      colorful: {
        backgroundColor: 'bg-gradient-to-r from-primary-500 to-primary-800',
        textColor: 'text-white',
        activeColor: 'text-yellow-300',
        shadow: true,
        border: false,
      },
    };
  }
}
